import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useChooseProgram, useFinishOnboarding, useOnboardingState } from '../../api/queries';
import { QueryBoundary } from '../../components/QueryBoundary';
import { useTranslation } from '../../i18n/I18nProvider';
import { useAuth } from '../../auth/AuthProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { SkeletonCard, useToast } from '../../ui';
import { Logo } from '../../components/Logo';
import { BASICS_FIELDS, BasicsStep } from './BasicsStep';
import { GoalStep } from './GoalStep';
import { ProgramStep } from './ProgramStep';

/** Koraci čarobnjaka onim redom kojim ih server broji u `onboarding_state.current_step`. */
export const WIZARD_STEPS = ['basics', 'goal', 'program'] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

/** Ukupan broj ekrana: prvi korak ima jedno polje po ekranu, druga dva po jedan. */
export const TOTAL_SCREENS = BASICS_FIELDS.length + WIZARD_STEPS.length - 1;

export function clampStep(step: number): number {
  if (!Number.isFinite(step)) return 0;
  return Math.min(Math.max(Math.trunc(step), 0), WIZARD_STEPS.length - 1);
}

/**
 * Čarobnjak nakon registracije. Trener je tražio da se „izbacuje jedan po jedan
 * prozorčić”, pa je svaki ekran jedno pitanje s trakom napretka.
 *
 * Korak se čita sa servera i nastavlja ondje gdje je stao — ponovna prijava
 * ne vraća klijenta na prvo pitanje.
 */
export function Onboarding() {
  const query = useOnboardingState();

  return (
    <div className="mx-auto w-full max-w-narrow px-5 py-8">
      <header className="mb-8 flex items-center justify-between">
        <Logo />
      </header>
      <QueryBoundary query={query} skeleton={<SkeletonCard />}>
        {(data) => <Wizard startStep={clampStep(data.currentStep)} />}
      </QueryBoundary>
    </div>
  );
}

function Wizard({ startStep }: { startStep: number }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { patchUser } = useAuth();
  const { toast } = useToast();
  const chooseProgram = useChooseProgram();
  const finish = useFinishOnboarding();

  const [step, setStep] = useState(startStep);
  const [fieldIndex, setFieldIndex] = useState(0);
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);

  const screenIndex = step === 0 ? fieldIndex : BASICS_FIELDS.length + (step - 1);

  async function onChoose(slug: string): Promise<void> {
    setPendingSlug(slug);
    try {
      await chooseProgram.mutateAsync(slug);
      // Upitnik je gotov tek s odabranim programom — gate u rutama tada pušta dalje.
      await finish.mutateAsync(WIZARD_STEPS.length);
      patchUser({ onboardingCompleted: true });
      toast(t('wizard.done'), 'success');
      void navigate('/', { replace: true });
    } catch (err) {
      toast(toErrorMessage(t, err).title, 'danger');
    } finally {
      setPendingSlug(null);
    }
  }

  if (step === 0) {
    return (
      <BasicsStep
        fieldIndex={fieldIndex}
        onFieldIndexChange={setFieldIndex}
        screenIndex={screenIndex}
        totalScreens={TOTAL_SCREENS}
        onDone={() => setStep(1)}
      />
    );
  }

  if (step === 1) {
    return (
      <GoalStep
        screenIndex={screenIndex}
        totalScreens={TOTAL_SCREENS}
        onDone={() => setStep(2)}
        onBack={() => {
          setFieldIndex(BASICS_FIELDS.length - 1);
          setStep(0);
        }}
      />
    );
  }

  return (
    <ProgramStep
      screenIndex={screenIndex}
      totalScreens={TOTAL_SCREENS}
      onChoose={(slug) => void onChoose(slug)}
      onBack={() => setStep(1)}
      pendingSlug={pendingSlug}
      error={chooseProgram.error ?? finish.error}
    />
  );
}
